import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { Receipt, Download, CheckCircle, Clock, FileText, TrendingUp, AlertCircle, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';
import TablePagination from '../components/TablePagination';

const VAT_PAGE_SIZE = 25;

export default function VATClaimsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exportedFilter, setExportedFilter] = useState('false');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('ReceiptDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [vatSearchTerm, setVatSearchTerm] = useState('');
  const [vatPage, setVatPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, [periodFilter, statusFilter, exportedFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build params
      const params = {};
      if (periodFilter) params.period = periodFilter;
      if (statusFilter) params.status = statusFilter;
      if (exportedFilter !== '') params.exported = exportedFilter;

      const [claimsRes, summaryRes] = await Promise.all([
        apiClient.get('/expenses/vat-claims', { params }),
        apiClient.get('/expenses/vat-claims/summary')
      ]);

      setClaims(claimsRes.data.claims || []);
      setSummary(summaryRes.data.summary || []);
    } catch (error) {
      console.error('Failed to fetch VAT claims:', error);
      alert('Failed to load VAT claims data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (period) => {
    if (!confirm(`Export all pending VAT claims for ${period} to CSV?`)) {
      return;
    }

    setExporting(true);

    try {
      const response = await apiClient.post('/expenses/vat-claims/export', {
        period
      });

      // Download CSV
      const blob = new Blob([response.data.csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert(`Successfully exported ${response.data.totalClaims} claims (£${response.data.totalVATAmount} VAT)`);

      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Failed to export VAT claims:', error);
      alert(error.response?.data?.message || 'Failed to export VAT claims');
    } finally {
      setExporting(false);
    }
  };

  const handleMarkClaimed = async (claimId, merchantName) => {
    const notes = prompt(`Mark claim from ${merchantName} as submitted to HMRC?\n\nEnter notes (optional):`);
    if (notes === null) return; // Cancelled

    try {
      await apiClient.patch(`/expenses/vat-claims/${claimId}/mark-claimed`, {
        notes: notes || undefined
      });

      alert('VAT claim marked as submitted');
      await fetchData();
    } catch (error) {
      console.error('Failed to mark claim:', error);
      alert(error.response?.data?.message || 'Failed to mark claim');
    }
  };

  const handleClearFilters = () => {
    setPeriodFilter('');
    setStatusFilter('');
    setExportedFilter('false');
  };

  const getStatusBadge = (status) => {
    const config = {
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      'Marked': { bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText },
      'Claimed': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      'Exported': { bg: 'bg-purple-100', text: 'text-purple-800', icon: Download },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-800', icon: X }
    };

    const cfg = config[status] || config['Pending'];
    const Icon = cfg.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {status}
      </span>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortedClaims = useMemo(() => {
    return [...claims].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (['NetAmount', 'VATAmount', 'Litres'].includes(sortField)) {
        aVal = parseFloat(aVal || 0);
        bVal = parseFloat(bVal || 0);
      } else if (sortField === 'ReceiptDate') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [claims, sortField, sortDirection]);

  const filteredVatClaims = useMemo(() => {
    if (!vatSearchTerm) return sortedClaims;
    const q = vatSearchTerm.toLowerCase();
    return sortedClaims.filter(c =>
      (c.EmployeeName || '').toLowerCase().includes(q) ||
      (c.MerchantName || '').toLowerCase().includes(q) ||
      (c.ExpenseNumber || '').toLowerCase().includes(q) ||
      (c.VATClaimStatus || '').toLowerCase().includes(q)
    );
  }, [sortedClaims, vatSearchTerm]);

  const pagedVatClaims = useMemo(
    () => filteredVatClaims.slice((vatPage - 1) * VAT_PAGE_SIZE, vatPage * VAT_PAGE_SIZE),
    [filteredVatClaims, vatPage]
  );
  React.useEffect(() => { setVatPage(1); }, [vatSearchTerm, sortField, sortDirection, periodFilter, statusFilter, exportedFilter]);

  // Group summary by period
  const summaryByPeriod = summary.reduce((acc, item) => {
    const period = item.VATReturnPeriod;
    if (!acc[period]) {
      acc[period] = {
        period,
        totalClaims: 0,
        totalVAT: 0,
        pendingClaims: 0,
        pendingVAT: 0,
        exportedClaims: 0,
        exportedVAT: 0,
        claimedClaims: 0,
        claimedVAT: 0
      };
    }

    acc[period].totalClaims += item.ClaimCount;
    acc[period].totalVAT += parseFloat(item.TotalVATClaimable || 0);

    if (item.VATClaimStatus === 'Pending') {
      acc[period].pendingClaims = item.ClaimCount;
      acc[period].pendingVAT = parseFloat(item.TotalVATClaimable || 0);
    } else if (item.VATClaimStatus === 'Exported') {
      acc[period].exportedClaims = item.ClaimCount;
      acc[period].exportedVAT = parseFloat(item.TotalVATClaimable || 0);
    } else if (item.VATClaimStatus === 'Claimed') {
      acc[period].claimedClaims = item.ClaimCount;
      acc[period].claimedVAT = parseFloat(item.TotalVATClaimable || 0);
    }

    return acc;
  }, {});

  const periods = Object.values(summaryByPeriod).sort((a, b) => b.period.localeCompare(a.period));

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Receipt className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">VAT Claims Dashboard</h1>
            </div>
            <p className="text-gray-600">Manage VAT reclaims from fuel receipts for HMRC MTD submission</p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
          >
            <Filter className="w-5 h-5" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period
                </label>
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All periods</option>
                  {periods.map(p => (
                    <option key={p.period} value={p.period}>{p.period}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Marked">Marked</option>
                  <option value="Exported">Exported</option>
                  <option value="Claimed">Claimed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Export Status
                </label>
                <select
                  value={exportedFilter}
                  onChange={(e) => setExportedFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="false">Not Exported</option>
                  <option value="true">Exported</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards by Period */}
      <div className="grid grid-cols-1 gap-6">
        {periods.map(period => (
          <div key={period.period} className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{period.period}</h2>
                    <p className="text-sm text-gray-600">
                      {period.totalClaims} total claims
                    </p>
                  </div>
                </div>

                {period.pendingClaims > 0 && (
                  <button
                    onClick={() => handleExport(period.period)}
                    disabled={exporting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export {period.pendingClaims} for MTD
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total VAT Claimable</p>
                <p className="text-2xl font-bold text-gray-900">
                  £{period.totalVAT.toFixed(2)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Pending</p>
                <p className="text-xl font-semibold text-yellow-600">
                  {period.pendingClaims} claims
                </p>
                <p className="text-sm text-yellow-700">£{period.pendingVAT.toFixed(2)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Exported</p>
                <p className="text-xl font-semibold text-purple-600">
                  {period.exportedClaims} claims
                </p>
                <p className="text-sm text-purple-700">£{period.exportedVAT.toFixed(2)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Claimed</p>
                <p className="text-xl font-semibold text-green-600">
                  {period.claimedClaims} claims
                </p>
                <p className="text-sm text-green-700">£{period.claimedVAT.toFixed(2)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Claims Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b flex flex-col sm:flex-row sm:items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 flex-1">
            VAT Claims ({filteredVatClaims.length}{vatSearchTerm ? ` of ${claims.length}` : ''})
          </h2>
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search claims..."
              value={vatSearchTerm}
              onChange={e => setVatSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            Loading VAT claims...
          </div>
        ) : claims.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No VAT claims found</p>
            <p className="text-sm mt-2">Claims will appear here when employees submit fuel receipts</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('ReceiptDate')}><span className="inline-flex items-center">Date <SortIcon field="ReceiptDate" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('EmployeeName')}><span className="inline-flex items-center">Employee <SortIcon field="EmployeeName" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('MerchantName')}><span className="inline-flex items-center">Merchant <SortIcon field="MerchantName" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fuel</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('NetAmount')}><span className="inline-flex items-center">Net <SortIcon field="NetAmount" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('VATAmount')}><span className="inline-flex items-center">VAT <SortIcon field="VATAmount" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('VATReturnPeriod')}><span className="inline-flex items-center">Period <SortIcon field="VATReturnPeriod" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('VATClaimStatus')}><span className="inline-flex items-center">Status <SortIcon field="VATClaimStatus" /></span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedVatClaims.map(claim => (
                  <tr key={claim.ExpenseVATClaimID} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(claim.ReceiptDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{claim.EmployeeName}</div>
                      <div className="text-xs text-gray-500">{claim.ExpenseNumber}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {claim.MerchantName || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {claim.FuelType && claim.Litres 
                        ? `${claim.Litres}L ${claim.FuelType}`
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      £{parseFloat(claim.NetAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        £{parseFloat(claim.VATAmount || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">{claim.VATRate}%</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-medium">
                      {claim.VATReturnPeriod}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(claim.VATClaimStatus)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {claim.VATClaimStatus === 'Exported' && (
                        <button
                          onClick={() => handleMarkClaimed(claim.ExpenseVATClaimID, claim.MerchantName)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Mark Claimed
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
          </table>
          </div>
          <TablePagination page={vatPage} pageSize={VAT_PAGE_SIZE} total={filteredVatClaims.length} onPageChange={setVatPage} />
          </>
        )}
      </div>

      {/* Help Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          VAT Reclaim Workflow
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>1. Pending:</strong> VAT claim created from fuel receipt OCR</p>
          <p><strong>2. Export:</strong> Click "Export for MTD" to generate CSV for quarterly VAT return</p>
          <p><strong>3. Submit:</strong> Upload CSV to HMRC Making Tax Digital portal</p>
          <p><strong>4. Mark Claimed:</strong> After HMRC processes, mark claims as submitted</p>
        </div>
      </div>
    </div>
  );
}
